import { type FC } from "react";
import { Card, CardHeader, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLiveEnabled } from "@/partner/entities/instance";
import {
  MetricCard,
  type MetricCardData,
} from "@/partner/shared/components/MetricCard";
import { formatNumber } from "@/shared/utils/formatNumber";
import { formatDuration } from "@/shared/utils/formatDuration";
import type { DashboardQueryType } from "@/types/graphql/partner.generated";

interface DashboardContentMetricsProps {
  dashboard: NonNullable<DashboardQueryType["dashboard"]>;
}

export const DashboardContentMetrics: FC<DashboardContentMetricsProps> = ({
  dashboard,
}) => {
  const { t } = useTranslation();
  const liveEnabled = useLiveEnabled();

  const cards: MetricCardData[] = [
    // Плитка завершённых эфиров только там, где эфиры включены: на
    // adult-инстансе раздела «Эфиры» нет, и ссылка вела бы в редирект.
    ...(liveEnabled
      ? [
          {
            primary: {
              label: t("dashboard.contentMetrics.finishedStreams"),
              value: formatNumber(dashboard.finishedStreamsCount),
              xs: 5,
            },
            secondary:
              dashboard.finishedStreamDuration > 60
                ? {
                    label: t("dashboard.contentMetrics.finishedStreamDuration"),
                    value:
                      formatDuration(dashboard.finishedStreamDuration) ?? "",
                    xs: 7,
                  }
                : undefined,
            to: "/partner/streams",
            xs: 12,
          },
        ]
      : []),
    {
      primary: {
        label: t("dashboard.contentMetrics.uploadedVideos"),
        value: formatNumber(dashboard.videosCount),
        xs: 5,
      },
      secondary:
        dashboard.videosDuration > 60
          ? {
              label: t("dashboard.contentMetrics.uploadedVideosDuration"),
              value: formatDuration(dashboard.videosDuration) ?? "",
              xs: 7,
            }
          : undefined,
      to: "/partner/videos",
      xs: 12,
    },
  ];

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Card>
          <CardHeader
            title={t("dashboard.contentMetrics.title")}
            subheader={t("dashboard.contentMetrics.subheader")}
          />
        </Card>
      </Grid>
      {cards.map((card, index) => (
        <Grid key={index} size={12}>
          <MetricCard metric={card} />
        </Grid>
      ))}
    </Grid>
  );
};
